import {
  useId,
  useState,
  type MouseEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn, StatCard } from './components';

// Form and data primitives used mostly by the backoffice. Same rules as components.tsx: ≥ 44 px targets, fluid
// layouts, text never truncated silently, states announced.

const CONTROL =
  'min-h-11 w-full rounded-lg border border-line bg-surface px-3 py-2 text-base text-ink disabled:bg-canvas disabled:text-muted';

function FieldFrame({
  id,
  label,
  hint,
  error,
  className,
  children,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly className?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      {children}
      {hint !== undefined && (
        <p id={`${id}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={`${id}-error`} className="text-sm break-words text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

const describedBy = (id: string, hint: string | undefined, error: string | undefined): string | undefined =>
  [hint === undefined ? undefined : `${id}-hint`, error === undefined ? undefined : `${id}-error`]
    .filter(Boolean)
    .join(' ') || undefined;

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  /** Text of the empty first option (no value). Omit when a value is always required and preselected. */
  readonly placeholder?: string;
}

export function SelectField({ label, hint, error, options, placeholder, className, ...rest }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} className={className}>
      <select
        id={id}
        {...rest}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(CONTROL, error !== undefined && 'border-danger')}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  );
}

export interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
}

export function TextAreaField({ label, hint, error, className, ...rest }: TextAreaFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} className={className}>
      <textarea
        id={id}
        rows={3}
        {...rest}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={cn(CONTROL, error !== undefined && 'border-danger')}
      />
    </FieldFrame>
  );
}

// ── Badge ──

type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';
const BADGE: Record<BadgeTone, string> = {
  neutral: 'bg-canvas text-ink border-line',
  info: 'bg-brand-50 text-brand-800 border-brand-100',
  success: 'bg-success-bg text-success border-success/30',
  warning: 'bg-warning-bg text-warning border-warning/30',
  danger: 'bg-occupied-bg text-danger border-danger/30',
};

export function Badge({ tone = 'neutral', children }: { readonly tone?: BadgeTone; readonly children: ReactNode }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-medium', BADGE[tone])}>
      {children}
    </span>
  );
}

// ── DataList: a table from 1024 px up, stacked cards below (phones and portrait tablets): the page never scrolls sideways ──

export interface Column<T> {
  readonly header: string;
  readonly cell: (row: T) => ReactNode;
  /** Right-aligned on wide screens (actions, numbers). */
  readonly end?: boolean;
}

export function DataList<T>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  onRowClick,
}: {
  /** Names the data for assistive technology (visually hidden). */
  readonly caption: string;
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly empty: ReactNode;
  /**
   * Clicking anywhere on the row does this (a mouse shortcut: the row must also hold a link or button that does the
   * same, which is what the keyboard and screen readers use). Clicks on a control inside the row are left to it.
   */
  readonly onRowClick?: (row: T) => void;
}) {
  const rowProps = (row: T) =>
    onRowClick === undefined
      ? {}
      : {
          onClick: (event: MouseEvent) => {
            if ((event.target as Element).closest('a, button, input, select, textarea, label') !== null) return;
            onRowClick(row);
          },
        };
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-line bg-surface px-4 py-8 text-center text-base text-muted">
        {empty}
      </div>
    );
  }
  return (
    <>
      <ul aria-label={caption} className="flex flex-col gap-3 lg:hidden">
        {rows.map((row) => (
          <li
            key={rowKey(row)}
            {...rowProps(row)}
            className={cn(
              'rounded-xl border border-line bg-surface p-4 shadow-sm',
              onRowClick !== undefined && 'cursor-pointer hover:border-brand-500',
            )}
          >
            <dl className="flex flex-col gap-2">
              {columns.map((column) => (
                <div key={column.header} className="flex flex-col gap-0.5">
                  <dt className="text-sm text-muted">{column.header}</dt>
                  <dd className="text-base break-words">{column.cell(row)}</dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto rounded-xl border border-line bg-surface shadow-sm lg:block">
        <table className="w-full table-auto border-collapse text-left text-base">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-line text-sm text-muted">
              {columns.map((column) => (
                <th key={column.header} scope="col" className={cn('px-4 py-3 font-medium', column.end && 'text-right')}>
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                {...rowProps(row)}
                className={cn(
                  'border-b border-line last:border-b-0',
                  onRowClick !== undefined && 'cursor-pointer hover:bg-canvas',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.header}
                    className={cn('px-4 py-3 align-middle break-words', column.end && 'text-right')}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Room grid: THE board of both apps (passenger, guardian, staff, hotel, coordinator) ──

export type SlotState = 'FREE' | 'OCCUPIED' | 'MINE' | 'HELD';

export interface GridSlot {
  readonly id: string;
  readonly position: number;
  readonly state: SlotState;
  readonly occupant?: { readonly memberId: string; readonly firstNames: string; readonly lastNames: string };
}

export interface GridRoom {
  readonly id: string;
  readonly label: string;
  readonly sexRestriction: 'FEMALE' | 'MALE' | 'ANY';
  readonly capacity: number;
  readonly physicalLabel?: string | null;
  readonly slots: readonly GridSlot[];
}

/** Who a room is for, in words (shared by the grid and the backoffice room list). */
export const ROOM_SEX_TEXT: Readonly<Record<GridRoom['sexRestriction'], string>> = {
  FEMALE: 'Mujeres',
  MALE: 'Varones',
  ANY: 'Mixta',
};

// State is never color-only: every bed says what it is in words (WCAG 1.4.1).
const SLOT_STYLE: Record<SlotState, { text: string; className: string }> = {
  FREE: { text: 'Libre', className: 'border-free-line bg-free-bg text-free' },
  OCCUPIED: { text: 'Ocupada', className: 'border-occupied-line bg-occupied-bg text-occupied' },
  MINE: { text: 'Tu plaza', className: 'border-mine bg-mine-bg text-mine' },
  HELD: { text: 'En espera', className: 'border-warning/40 bg-warning-bg text-warning' },
};

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)
    .toUpperCase();

/**
 * The server decides what each viewer may see (staff, guardians, hotel and guide: everything; a passenger: the names
 * of their roommates and their own room's hotel number). The grid shows whatever it receives.
 */
export function RoomGrid<R extends GridRoom>({
  rooms,
  slotAction,
  roomFooter,
}: {
  readonly rooms: readonly R[];
  /**
   * What clicking a slot does, if anything: the verb is appended to the slot's accessible name. `text` is what a FREE
   * bed shows instead of "Libre" (one label only: "+ Elegir cama").
   */
  readonly slotAction?: (
    room: R,
    slot: GridSlot,
  ) => { readonly verb: string; readonly text?: string; readonly run: () => void } | undefined;
  readonly roomFooter?: (room: R) => ReactNode;
}) {
  return (
    <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {rooms.map((room) => {
        const free = room.slots.filter((slot) => slot.state === 'FREE').length;
        return (
          <li key={room.id} className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-lg font-bold break-words">{room.label}</h2>
                <p className="text-sm text-muted">
                  {ROOM_SEX_TEXT[room.sexRestriction]} · {room.capacity - free} de {room.capacity} ocupadas
                </p>
              </div>
              <span className={cn('shrink-0 text-sm font-bold', free > 0 ? 'text-free' : 'text-muted')}>
                {free === 0 ? 'Completa' : `${String(free)} ${free === 1 ? 'libre' : 'libres'}`}
              </span>
            </div>
            {/* The line is always there, empty when this viewer may not see the hotel's number (a passenger sees only
                their own room's): every card keeps its beds at the same height (owner, 2026-10-07). */}
            <p className="min-h-5 text-sm text-muted">
              {room.physicalLabel === undefined
                ? null
                : `Habitación del hotel: ${room.physicalLabel ?? 'todavía no cargada'}`}
            </p>
            <ul className="grid grid-cols-2 gap-2">
              {room.slots.map((slot) => {
                const style = SLOT_STYLE[slot.state];
                const who =
                  slot.occupant !== undefined && slot.state !== 'MINE'
                    ? `${slot.occupant.firstNames} ${slot.occupant.lastNames}`
                    : undefined;
                const label = `Plaza ${String(slot.position)}: ${style.text}${who === undefined ? '' : `, ${who}`}`;
                const action = slotAction?.(room, slot);
                return (
                  <li key={slot.id}>
                    <button
                      type="button"
                      aria-label={action === undefined ? label : `${label}. ${action.verb}`}
                      disabled={action === undefined}
                      onClick={action?.run}
                      className={cn(
                        'flex min-h-24 w-full flex-col justify-center gap-2 rounded-xl border p-3 text-left text-sm',
                        style.className,
                        action === undefined ? 'cursor-default' : 'cursor-pointer hover:brightness-95',
                      )}
                    >
                      {slot.state === 'FREE' ? (
                        // One label only (no "Libre" next to the action).
                        <span className="font-bold">{action?.text ?? style.text}</span>
                      ) : (
                        <>
                          <span className="flex items-center gap-2 text-xs font-bold">
                            <span
                              aria-hidden="true"
                              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface/70 text-[10px]"
                            >
                              {slot.state === 'MINE' ? '★' : who === undefined ? '●' : initialsOf(who)}
                            </span>
                            {style.text}
                          </span>
                          {who !== undefined && (
                            <span className="block w-full text-sm font-semibold break-words">Ocupada por {who}</span>
                          )}
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {roomFooter === undefined ? null : <div className="mt-auto">{roomFooter(room)}</div>}
          </li>
        );
      })}
    </ul>
  );
}

// ── Distribution progress: how many passengers already have a slot, and who is missing ──

export function DistributionProgress({
  assigned,
  passengers,
  percent,
  freeSlots,
  missing,
}: {
  readonly assigned: number;
  readonly passengers: number;
  readonly percent: number;
  readonly freeSlots: number;
  readonly missing: readonly { readonly id: string; readonly text: string }[];
}) {
  return (
    <section aria-label="Progreso" className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard caption="Total pasajeros" value={String(passengers)} />
        <StatCard caption="Plazas asignadas" value={`${String(assigned)} / ${String(passengers)}`} />
        <StatCard caption="Progreso" value={`${String(percent)}%`}>
          <div
            role="progressbar"
            aria-label="Pasajeros con plaza"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
            className="mt-4 h-3 w-full overflow-hidden rounded-full bg-canvas"
          >
            <div className="h-full rounded-full bg-brand-600" style={{ width: `${String(percent)}%` }} />
          </div>
        </StatCard>
      </div>
      <p className="text-base" aria-live="polite">
        <strong>{assigned}</strong> de {passengers} pasajeros con plaza ({percent}%) · {freeSlots} plazas libres
      </p>
      {missing.length > 0 && (
        <>
          <h3 className="text-base font-semibold">Sin plaza ({missing.length})</h3>
          <ul className="flex flex-wrap gap-2">
            {missing.map((member) => (
              <li key={member.id} className="rounded-full border border-line bg-canvas px-3 py-1 text-sm">
                {member.text}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

// ── Confirmation of the distribution: confirmed / can be confirmed / what is still missing ──

export function ConfirmationPanel({
  confirmedText,
  thresholdPercent,
  percent,
  confirmable,
  viewerIsSigner,
  action,
}: {
  /** "Confirmada el … con 18 de 20 pasajeros con plaza." — present when a confirmation is in force. */
  readonly confirmedText?: string | undefined;
  readonly thresholdPercent: number;
  readonly percent: number;
  readonly confirmable: boolean;
  readonly viewerIsSigner: boolean;
  /** The button of whoever may act now (confirm for the signer, reopen for an administrator). */
  readonly action?: ReactNode;
}) {
  const confirmed = confirmedText !== undefined;
  return (
    <section
      aria-label="Confirmación de la distribución"
      className={cn(
        'flex flex-col gap-3 rounded-2xl border p-5 sm:flex-row sm:items-center sm:justify-between',
        confirmed ? 'border-success/30 bg-success-bg' : 'border-line bg-surface',
      )}
    >
      <div className="min-w-0">
        <h2 className="text-lg font-semibold">{confirmed ? 'Distribución confirmada' : 'Confirmación'}</h2>
        <p className="mt-1 text-base break-words" aria-live="polite">
          {confirmed
            ? `${confirmedText} Nadie puede cambiar de habitación; solo un administrador de Funtour puede reabrirla.`
            : confirmable
              ? viewerIsSigner
                ? `Ya se alcanzó el ${String(thresholdPercent)} % de pasajeros con plaza: podés confirmar la distribución.`
                : `Ya se alcanzó el ${String(thresholdPercent)} %: el firmante del grupo puede confirmarla.`
              : `Se puede confirmar cuando el ${String(thresholdPercent)} % de los pasajeros tenga plaza (ahora ${String(percent)} %). La confirma el apoderado firmante del grupo.`}
        </p>
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </section>
  );
}

// ── Creatable combobox (prompt21): search an existing value or create a new one with Enter ──

export interface ComboOption {
  readonly value: string;
  readonly label: string;
}

/**
 * A searchable list that can also CREATE what it does not find (WAI-ARIA combobox with a listbox popup). Typing filters
 * the options; ↑/↓ move, Enter picks the highlighted one — or, when nothing matches exactly, calls `onCreate` with the
 * typed text and selects what it returns; Escape closes. The chosen value is announced below the field.
 */
export function CreatableCombobox({
  label,
  hint,
  options,
  value,
  onChange,
  onCreate,
  createLabel = (text) => `Crear «${text}»`,
  disabled = false,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly options: readonly ComboOption[];
  /** The selected option's value ('' = none). */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Creates the typed value; resolves to the new option's value, or undefined if it failed (the caller reports why). */
  readonly onCreate: (text: string) => Promise<string | undefined>;
  readonly createLabel?: (text: string) => string;
  readonly disabled?: boolean;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const selected = options.find((option) => option.value === value);
  // What the person is typing; undefined = not editing, so the field shows the chosen option (or nothing). Derived,
  // not copied by an effect: a selection or a reset made from outside shows up at once.
  const [typed, setTyped] = useState<string>();
  const text = typed ?? selected?.label ?? '';
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [creating, setCreating] = useState(false);

  const needle = text.trim().toLowerCase();
  const matches = options.filter((option) => needle === '' || option.label.toLowerCase().includes(needle));
  const exact = options.find((option) => option.label.trim().toLowerCase() === needle);
  const canCreate = needle !== '' && exact === undefined;
  const items: readonly ({ readonly kind: 'option'; readonly option: ComboOption } | { readonly kind: 'create' })[] = [
    ...matches.slice(0, 50).map((option) => ({ kind: 'option' as const, option })),
    ...(canCreate ? [{ kind: 'create' as const }] : []),
  ];
  const current = Math.min(active, Math.max(items.length - 1, 0));

  const pick = (option: ComboOption): void => {
    onChange(option.value);
    setTyped(undefined);
    setOpen(false);
  };
  const create = async (): Promise<void> => {
    const typed = text.trim();
    if (typed === '' || creating) return;
    setCreating(true);
    try {
      const created = await onCreate(typed);
      if (created !== undefined) {
        onChange(created);
        setTyped(undefined);
        setOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };
  const choose = (index: number): void => {
    const item = items[index];
    if (item === undefined) return;
    if (item.kind === 'option') pick(item.option);
    else void create();
  };

  return (
    <div className="relative flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open && items.length > 0}
        aria-controls={listId}
        aria-activedescendant={open && items.length > 0 ? `${id}-item-${String(current)}` : undefined}
        aria-describedby={`${id}-status${hint === undefined ? '' : ` ${id}-hint`}`}
        aria-busy={creating || undefined}
        disabled={disabled || creating}
        value={text}
        className={CONTROL}
        onChange={(event) => {
          setTyped(event.target.value);
          setActive(0);
          setOpen(true);
          if (value !== '') onChange('');
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onBlur={() => {
          // Leaving the field on an exact name selects it; anything else stays unselected (never created by accident).
          setOpen(false);
          if (value === '' && exact !== undefined) pick(exact);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActive((index) => Math.min(index + 1, items.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(index - 1, 0));
          } else if (event.key === 'Enter') {
            // Enter never submits the surrounding form from here: it picks or creates.
            event.preventDefault();
            if (exact !== undefined && (!open || items[current]?.kind !== 'option')) pick(exact);
            else if (open && items.length > 0) choose(current);
            else if (canCreate) void create();
          } else if (event.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && items.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          className="absolute top-full z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-line bg-surface py-1 shadow-lg"
        >
          {items.map((item, index) => (
            <li
              key={item.kind === 'option' ? item.option.value : '__create'}
              id={`${id}-item-${String(index)}`}
              role="option"
              aria-selected={index === current}
              // mousedown, not click: it runs before the input's blur closes the list.
              onMouseDown={(event) => {
                event.preventDefault();
                choose(index);
              }}
              className={cn(
                'flex min-h-11 cursor-pointer items-center px-3 text-base',
                index === current ? 'bg-brand-50 text-ink' : 'text-ink',
                item.kind === 'create' && 'font-semibold text-brand-700',
              )}
            >
              {item.kind === 'option' ? item.option.label : createLabel(text.trim())}
            </li>
          ))}
        </ul>
      )}
      {hint !== undefined && (
        <p id={`${id}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      )}
      <p id={`${id}-status`} aria-live="polite" className="text-sm text-muted">
        {creating ? 'Creando…' : selected === undefined ? 'Nada elegido todavía.' : `Elegido: ${selected.label}`}
      </p>
    </div>
  );
}
